import React from 'react'
import { Check } from 'lucide-react'
import { getImageSrc } from 'services/util'
import { canSelectShow, MAX_ITEMS } from './showHelpers'
import { handleShowSelection, SourceTypeState } from './sourceTypeHelpers'
import CardDetails from './CardDetails'

interface SelectableShowCardsProps {
  filteredShows: any[]
  isFilteringShows: boolean
  sourceType: 'Category' | 'Collection'
  currentState: SourceTypeState
  setState: (state: SourceTypeState) => void
  onSave: () => void
}

export const SelectableShowCards: React.FC<SelectableShowCardsProps> = ({
  filteredShows,
  isFilteringShows,
  sourceType,
  currentState,
  setState,
  onSave
}) => {
  const { selectedShowIds, cardDetails } = currentState

  return (
    <div className="space-y-4 lg:col-span-3">
      <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
            Available Shows ({filteredShows.length}) - Select up to {MAX_ITEMS}
          </h3>
          <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
            <span className="font-medium">Selected:</span>
            <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
              {selectedShowIds.length}/{MAX_ITEMS}
            </span>
          </div>
        </div>
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {filteredShows.map((show: any) => {
            const showId = String(show.show?.id || show.id)
            const showData = show.show || show
            const isSelected = selectedShowIds.includes(showId)
            const canSelect = canSelectShow(showId, selectedShowIds, MAX_ITEMS)

            return (
              <div
                key={showId}
                onClick={() => handleShowSelection(show, showId, isSelected, currentState, setState)}
                className={`
                  relative flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all
                  ${isSelected
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : canSelect
                      ? 'border-slate-200 hover:border-blue-300 hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-800'
                      : 'border-slate-200 opacity-50 cursor-not-allowed dark:border-slate-600'
                  }
                `}
              >
                {isSelected && (
                  <div className="absolute top-2 right-2">
                    <div className="bg-blue-500 rounded-full p-1">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                  </div>
                )}
                {showData.thumbnail && (
                  <img
                    src={getImageSrc(showData.thumbnail)}
                    alt={showData.name || 'Show thumbnail'}
                    className="w-16 h-12 object-cover rounded border flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  {showData.name && (
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                      {showData.name}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Show ID: {showId}
                  </p>
                </div>
                {!canSelect && !isSelected && (
                  <p className="text-xs text-red-500 flex-shrink-0">
                    Maximum {MAX_ITEMS} shows allowed
                  </p>
                )}
              </div>
            )
          })}
        </div>
        {filteredShows.length === 0 && !isFilteringShows && (
          <p className="text-sm text-slate-500 text-center py-4">
            No shows found with more than 2 episodes
          </p>
        )}
        {isFilteringShows && (
          <p className="text-sm text-slate-500 text-center py-4">
            Filtering shows...
          </p>
        )}
      </div>

      {selectedShowIds.length > 0 && (
        <CardDetails
          showIds={selectedShowIds}
          cardDetails={cardDetails}
          onCardDetailsChange={(details) => {
            // Handle both function and value forms of setState
            if (typeof details === 'function') {
              setState((prev) => ({
                ...prev,
                cardDetails: details(prev.cardDetails)
              }))
            } else {
              setState({ ...currentState, cardDetails: details })
            }
          }}
          onSave={onSave}
        />
      )}
    </div>
  )
}
